/**
 * WagePro-branded, one-step admin signup screen (WagePro signup epic).
 *
 * Before this screen, a new WagePro admin had to go through Ledger's generic
 * two-step flow (`auth.register` on the main dashboard, then a separate
 * "Practice" setup screen to name a firm) before a payroll "company" even
 * existed. This screen collapses that into one request — email, password,
 * full name, and the company name — via `wageproAuth.signup`
 * (`apps/api/src/trpc/routers/wagepro_auth.ts`), which sets the session
 * cookie directly (WagePro standalone-admin-auth pivot off OAuth — no
 * redirect, no consent screen, no Ledger branding, ever). This screen then
 * just navigates to `/`, same as any other cookie-authenticated app.
 *
 * Rendered by `main.tsx` BEFORE `<AuthProvider><AuthGate/></AuthProvider>`
 * mounts, gated on `window.location.pathname === SIGNUP_PATH` — same
 * pre-auth pathname-gate mechanism as `apps/tax-web/src/App.tsx`'s
 * `EssAcceptScreen` / this app's own `InviteAcceptScreen`.
 *
 * Wrapped in `WageProAuthLayout` (split-pane brand chrome matching Ledger's
 * own login page archetype, restyle pass added per direct user request) —
 * this component supplies only the form content, not the page shell.
 */
import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import {
  Button,
  Checkbox,
  FormField,
  GlassCard,
  Input,
  PasswordInput,
} from '@jumpstone/ui-react';
import { getTrpcClient } from '../../lib/trpc.js';
import { isNative } from '../../lib/platform.js';
import {
  nativeClientInput,
  persistNativeSession,
  type NativeSessionCarrier,
} from '../../lib/auth/native_shell_auth.js';
import { setNativeSessionToken } from '../../lib/auth/native_session_store.js';
import { performCookieLoginPostAction } from '../../lib/auth/auth_context.js';
import { clearStoredTokens } from '../../lib/auth/token_store.js';
import { LOGIN_PATH } from '../../lib/auth/wagepro_login_logic.js';
import { AuthAltActionLink } from './auth_alt_action_link.js';
import { AuthFormError } from './auth_form_error.js';
import { WageProAuthLayout } from '../../components/layout/wagepro_auth_layout.js';
import type { AuthErrorDisplay } from '../../lib/auth/auth_error_display.js';
import {
  classifySignupError,
  hasSignupFieldErrors,
  validateSignupForm,
  type SignupFieldErrors,
  type WageProSignupFormValues,
} from '../../lib/auth/wagepro_signup_logic.js';

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; error: AuthErrorDisplay };

const EMPTY_VALUES: WageProSignupFormValues = {
  email: '',
  password: '',
  confirm_password: '',
  full_name: '',
  company_name: '',
  accept_tos: false,
  accept_privacy: false,
};

/** The exact fields `wageproAuth.signup` takes — the confirmation is not one of them. */
export interface WageProSignupRequest {
  email: string;
  password: string;
  full_name: string;
  company_name: string;
  accept_tos: true;
  accept_privacy: true;
  client?: 'native-shell';
}

function signupViaTrpc(
  input: WageProSignupRequest,
): Promise<NativeSessionCarrier> {
  return getTrpcClient().wageproAuth.signup.mutate(input);
}

export function WageProSignupScreen({
  onSignedUp,
  signup = signupViaTrpc,
  navigate = (path: string) => {
    window.location.href = path;
  },
}: {
  /** Called after `wageproAuth.signup` succeeds, right before navigating to `/`. */
  onSignedUp?: () => void;
  /** Test seam for `wageproAuth.signup`. */
  signup?: (input: WageProSignupRequest) => Promise<NativeSessionCarrier>;
  /** Test seam for the post-signup full navigation. */
  navigate?: (path: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const [values, setValues] = useState<WageProSignupFormValues>(EMPTY_VALUES);
  const [fieldErrors, setFieldErrors] = useState<SignupFieldErrors>({});
  const [state, setState] = useState<SubmitState>({ kind: 'idle' });

  function set<K extends keyof WageProSignupFormValues>(
    key: K,
    value: WageProSignupFormValues[K],
  ): void {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(): Promise<void> {
    const errors = validateSignupForm(values);
    setFieldErrors(errors);
    if (hasSignupFieldErrors(errors)) return;

    setState({ kind: 'submitting' });
    // ONLY the mutation is guarded. The success side effects below must stay
    // OUTSIDE this try: `classifyAuthError` reads a bare `TypeError` as a
    // transport failure, so a null-deref thrown by an `onSignedUp` consumer
    // would tell an already-authenticated user to check their internet
    // connection — a confidently wrong diagnosis, and one that sends them to
    // debug their network instead of reporting a real bug.
    const native = isNative();
    // Only the token field is needed here; typing it as the narrow carrier
    // keeps this screen from depending on the full router result shape.
    let result: NativeSessionCarrier;
    try {
      result = await signup({
        email: values.email.trim(),
        password: values.password,
        full_name: values.full_name.trim(),
        company_name: values.company_name.trim(),
        accept_tos: true,
        accept_privacy: true,
        ...nativeClientInput(native),
      });
    } catch (err) {
      setState({ kind: 'error', error: classifySignupError(err) });
      return;
    }

    // Clear any stale WagePro-audience OAuth bearer token pair before
    // navigating — same reason as the equivalent call in
    // `wagepro_login_screen.tsx`: `resolveAuthBoot`'s grant-first branch
    // (#24241) would otherwise consult the leftover token ahead of
    // `wageproAuth.me` and boot the new owner into whatever organisation
    // the stale token grants instead of the company they just signed up
    // for.
    await performCookieLoginPostAction({
      clearBearerTokens: () => clearStoredTokens(),
    });

    // Deliberately OUTSIDE the try above, and NOT classified with
    // `classifyAuthError` — see the fuller note in
    // `wagepro_login_screen.tsx`: a Capacitor bridge rejection is often a
    // `TypeError`, which would be misreported as a network failure to a user
    // whose company was just created successfully.
    //
    // This is the exact path the shipped Android app failed on: company
    // created, cookie dropped cross-site, straight into a `/login` loop.
    let persisted: boolean;
    try {
      persisted = await persistNativeSession(result, {
        native,
        setToken: (token) => setNativeSessionToken(token, { native }),
      });
    } catch (err) {
      console.error('[wagepro-web] storing the native session failed:', err);
      persisted = false;
    }
    if (!persisted) {
      setState({ kind: 'error', error: { kind: 'unknown' } });
      return;
    }
    onSignedUp?.();
    // On web the session cookie `wageproAuth.signup` just set IS the login;
    // in the native shell it is the token persisted just above. Either way,
    // no OAuth hop — plain navigation into the app.
    navigate('/');
  }

  const submitting = state.kind === 'submitting';

  return (
    <WageProAuthLayout>
      <GlassCard variant="strong" className="rounded-2xl p-8 w-full">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-[color:var(--ink)]">
            {t('signup.title')}
          </h1>
          <p className="mt-1 text-sm text-[color:var(--ink-muted)]">
            {t('signup.dek')}
          </p>
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            void handleSubmit();
          }}
          className="flex flex-col gap-4"
        >
          <FormField
            label={t('signup.company_name_label')}
            htmlFor="signup-company-name"
            helpText={t('signup.company_name_hint')}
            error={fieldErrors.company_name && t('signup.company_name_error')}
          >
            <Input
              id="signup-company-name"
              autoComplete="organization"
              value={values.company_name}
              onChange={(event) => set('company_name', event.target.value)}
              disabled={submitting}
            />
          </FormField>

          <FormField
            label={t('signup.full_name_label')}
            htmlFor="signup-full-name"
            error={fieldErrors.full_name && t('signup.full_name_error')}
          >
            <Input
              id="signup-full-name"
              autoComplete="name"
              value={values.full_name}
              onChange={(event) => set('full_name', event.target.value)}
              disabled={submitting}
            />
          </FormField>

          <FormField
            label={t('signup.email_label')}
            htmlFor="signup-email"
            error={fieldErrors.email && t('signup.email_error')}
          >
            <Input
              id="signup-email"
              type="email"
              autoComplete="email"
              value={values.email}
              onChange={(event) => set('email', event.target.value)}
              disabled={submitting}
            />
          </FormField>

          <FormField
            label={t('signup.password_label')}
            htmlFor="signup-password"
            helpText={t('signup.password_help')}
            error={fieldErrors.password && t('signup.password_error')}
          >
            <PasswordInput
              id="signup-password"
              autoComplete="new-password"
              value={values.password}
              onChange={(event) => set('password', event.target.value)}
              disabled={submitting}
              labels={{
                show_password: t('signup.show_password'),
                hide_password: t('signup.hide_password'),
              }}
            />
          </FormField>

          <FormField
            label={t('auth.confirm_password_label')}
            htmlFor="signup-confirm-password"
            error={
              fieldErrors.confirm_password && t('auth.confirm_password_error')
            }
          >
            <PasswordInput
              id="signup-confirm-password"
              autoComplete="new-password"
              value={values.confirm_password}
              onChange={(event) => set('confirm_password', event.target.value)}
              disabled={submitting}
              labels={{
                show_password: t('signup.show_password'),
                hide_password: t('signup.hide_password'),
              }}
            />
          </FormField>

          {/* ONE consent control for both documents.
              Two stacked "I agree to ..." rows read as bureaucratic friction on
              the last step before signup, and nothing was gained by splitting
              them: neither could be declined independently — the form refuses to
              submit without both.

              The WIRE CONTRACT is unchanged: this still sets `accept_tos` AND
              `accept_privacy`, so `wageproAuth.signup` records
              `tos_version`/`tos_version_accepted_at` and
              `privacy_version`/`privacy_version_accepted_at` separately, exactly
              as before (GDPR Art. 7 — the acceptance record must name the
              document VERSION accepted, and that stays per-document regardless
              of how many checkboxes collected it).

              Note this is document acceptance, not consent to a processing
              purpose — the latter (`marketing_consent`) is a separate opt-in
              elsewhere and must never be folded into this box. */}
          <label className="flex items-start gap-2 text-sm text-[color:var(--ink)]">
            <Checkbox
              checked={values.accept_tos && values.accept_privacy}
              onCheckedChange={(checked) => {
                setValues((prev) => ({
                  ...prev,
                  accept_tos: checked,
                  accept_privacy: checked,
                }));
              }}
              disabled={submitting}
            />
            <span>{t('auth.accept_terms')}</span>
          </label>
          {(fieldErrors.accept_tos || fieldErrors.accept_privacy) && (
            <p role="alert" className="text-[13px] text-[var(--color-danger)]">
              {t('auth.accept_terms_error')}
            </p>
          )}

          {state.kind === 'error' && <AuthFormError error={state.error} />}

          <Button
            type="submit"
            variant="primary"
            loading={submitting}
            className="mt-2 w-full justify-center"
          >
            {submitting ? t('signup.submitting') : t('signup.submit')}
          </Button>
        </form>

        <AuthAltActionLink
          href={LOGIN_PATH}
          label={t('signup.back_to_sign_in')}
        />
      </GlassCard>
    </WageProAuthLayout>
  );
}
